import type { Provider, ProviderStatus } from "@aiyo/shared";
import { Alert, Button, Table, toast } from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormDialog } from "../components/dialogs";
import { SelectInput, TextInput } from "../components/fields";
import { ChannelChip, Code, ErrorBlock, LoadingBlock, Page, PageHeader, StateChip } from "../components/ui";
import { useFormat } from "../lib/format";
import { useLastDefined } from "../lib/hooks";
import { useMe, useProviders, useUpdateProvider } from "../lib/queries";

const STATUSES: ProviderStatus[] = ["active", "degraded", "disabled"];

export function ProvidersPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const providers = useProviders();
  const { data: me } = useMe();
  const [editing, setEditing] = useState<Provider | null>(null);
  const isSuperadmin = Boolean(me?.is_superadmin);

  return (
    <Page>
      <PageHeader title={t("providers.title")} description={t("providers.subtitle")} />
      {!isSuperadmin ? (
        <Alert>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>{t("providers.superadminOnly")}</Alert.Description>
          </Alert.Content>
        </Alert>
      ) : null}
      {providers.isPending ? (
        <LoadingBlock />
      ) : providers.isError ? (
        <ErrorBlock error={providers.error} onRetry={() => providers.refetch()} />
      ) : (
        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label={t("providers.title")} className="min-w-[800px]">
              <Table.Header>
                <Table.Column isRowHeader>{t("common.name")}</Table.Column>
                <Table.Column>{t("providers.code")}</Table.Column>
                <Table.Column>{t("providers.channel")}</Table.Column>
                <Table.Column>{t("common.status")}</Table.Column>
                <Table.Column>{t("providers.note")}</Table.Column>
                <Table.Column>{t("common.updated")}</Table.Column>
                <Table.Column className="text-end">{t("common.actions")}</Table.Column>
              </Table.Header>
              <Table.Body>
                {providers.data.map((p) => (
                  <Table.Row key={p.id} id={p.id}>
                    <Table.Cell className="font-medium">
                      {p.name}
                      {p.capabilities.mock ? (
                        <span className="ml-2 text-xs text-muted">({t("providers.mock")})</span>
                      ) : null}
                    </Table.Cell>
                    <Table.Cell>
                      <Code>{p.code}</Code>
                    </Table.Cell>
                    <Table.Cell>
                      <ChannelChip channel={p.channel} />
                    </Table.Cell>
                    <Table.Cell>
                      <StateChip
                        ok={p.status === "active" ? true : p.status === "degraded" ? "warn" : false}
                        label={t(`status.provider.${p.status}`)}
                      />
                    </Table.Cell>
                    <Table.Cell className="max-w-[220px] truncate text-sm text-muted">
                      {p.status_note ?? ""}
                    </Table.Cell>
                    <Table.Cell className="text-sm whitespace-nowrap">
                      {fmt.relative(p.updated_at)}
                    </Table.Cell>
                    <Table.Cell className="text-end">
                      {isSuperadmin ? (
                        <Button size="sm" variant="ghost" onPress={() => setEditing(p)}>
                          {t("providers.setStatus")}
                        </Button>
                      ) : null}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}
      <ProviderStatusDialog provider={editing} onClose={() => setEditing(null)} />
    </Page>
  );
}

function ProviderStatusDialog({ provider, onClose }: { provider: Provider | null; onClose: () => void }) {
  const { t } = useTranslation();
  const shown = useLastDefined(provider);
  const update = useUpdateProvider();
  const [status, setStatus] = useState<ProviderStatus>("active");
  const [note, setNote] = useState("");

  // biome-ignore lint/correctness/useExhaustiveDependencies: the form resets only when the dialog target changes.
  useEffect(() => {
    if (!provider) return;
    update.reset();
    setStatus(provider.status);
    setNote(provider.status_note ?? "");
  }, [provider]);

  return (
    <FormDialog
      isOpen={provider !== null}
      onOpenChange={(open) => !open && onClose()}
      title={shown?.name}
      submitLabel={t("common.save")}
      submitVariant={status === "disabled" ? "danger" : "primary"}
      isPending={update.isPending}
      error={update.error}
      onSubmit={() =>
        provider &&
        update.mutate(
          { id: provider.id, status, status_note: note.trim() || null },
          {
            onSuccess: () => {
              toast.success(t("providers.updated"));
              onClose();
            },
          },
        )
      }
    >
      <SelectInput
        label={t("common.status")}
        description={t(`providers.statusHelp.${status}`)}
        value={status}
        onChange={(v) => v && setStatus(v as ProviderStatus)}
        options={STATUSES.map((s) => ({ id: s, label: t(`status.provider.${s}`) }))}
      />
      <TextInput
        name="note"
        label={`${t("providers.note")} (${t("common.optional").toLowerCase()})`}
        placeholder={t("providers.notePlaceholder")}
        value={note}
        onChange={setNote}
      />
    </FormDialog>
  );
}
